import _, { get } from 'lodash';
import { Box } from '@material-ui/core';
import { equipmentAPI } from '../../../../apis';
import { getUUID } from '../../../../utils';
import Text from '../../../../components/Text';

// 获取Central store里面可用设备数量
const getCentralStoreAvailableQty = (categorys = [], selectedCategory) => {
  if (!selectedCategory?.category) return null;
  const currentCategory = _.find(categorys, (item) => item?.id === selectedCategory?.category);
  return currentCategory?.availableQty;
};

// 获取Satellite store里面可用设备的数量
const getSatelliteStoreAvailableQty = (categorys = [], selectedCategory) => {
  if (!selectedCategory?.category) return null;
  const satelliteStores = _.find(categorys, (item) => item?.id === selectedCategory?.category)?.satelliteStores;
  return satelliteStores?.[0]?.satelliteStoresQty;
};

// 从category的satelliteStores里面筛选只属于当前provider的
const getCategoryByProvider = (providerItem) => {
  const categorys = _.map(providerItem, (equipmentItem) => equipmentItem.categorys).flat();

  // 根据 hospitalCategoryId 去除categorys里面重复的类型
  const deduplicateCategorys = _.unionBy(categorys, 'id');
  const provideWardName = providerItem?.[0]?.provideWardName;

  return _.map(deduplicateCategorys, (categoryItem) => {
    const currentSatelliteStores = _.filter(
      categoryItem?.satelliteStores,
      (satelliteStoreItem) => satelliteStoreItem?.satelliteStoresName === provideWardName
    );
    return {
      ...categoryItem,
      categorySatelliteStoresId: currentSatelliteStores?.[0]?.categorySatelliteStoresId ?? null,
      satelliteStores: currentSatelliteStores,
    };
  });
};

// 把所有设备按Satellite Store名字group在一起
const groupByProviderName = (providerList, selectedCategory) => {
  const providerGroups = _.groupBy(providerList, (provider) => provider.provideWardName);
  let list = [];
  _.mapValues(providerGroups, (providerItem) => {
    const categoryList = getCategoryByProvider(providerItem);
    const satelliteStoreQty = getSatelliteStoreAvailableQty(categoryList, selectedCategory);
    const newProviderItem = {
      ...providerItem?.[0],
      text: `${providerItem?.[0]?.text}${satelliteStoreQty !== null ? ` (Qty: ${satelliteStoreQty})` : ``} `,
      categorys: categoryList,
      availableQty: satelliteStoreQty ?? 0,
    };
    list.push(newProviderItem);
  });
  return list;
};

// 获取每个provider下的SatelliteStore
const getSatelliteStoreOptions = (providerItem, equipmentAsset) => {
  if (!_.isArray(providerItem?.categorys)) return [];
  // 拿出每个provider里面的所有设备类型里的所有Satellite Store
  let satelliteStores = [];
  _.forEach(providerItem?.categorys, (categoryItem) => {
    if (_.isArray(categoryItem?.satelliteStores)) {
      satelliteStores = satelliteStores.concat(categoryItem?.satelliteStores);
    }
  });
  const satelliteStoreOptions = _.map(satelliteStores, (satelliteStoreItem) => ({
    text: `(${providerItem?.wardName}) ${satelliteStoreItem.satelliteStoresName}`,
    value: `${providerItem.wardId}-${satelliteStoreItem.categorySatelliteStoresId}`,
    tagId: providerItem.wardId,
    categorys: _.filter(providerItem.categorys, (item) => satelliteStoreItem.hospitalCategoryId === item.id),
    wardId: providerItem.wardId,
    provideWardName: satelliteStoreItem.satelliteStoresName,
    group: providerItem?.wardName,
  }));

  const groupSatelliteStoreOptions = groupByProviderName(satelliteStoreOptions, equipmentAsset);
  return groupSatelliteStoreOptions;
};

// for Lend / Borrow New Loan Request Popup Provider options
const getNewRequestProviderOptions = (params) => {
  const { type, equipmentAssets, flag, providerList, currentWardId } = params;
  if (!_.isArray(providerList)) return [];

  const selectedEquipment = equipmentAssets?.[flag];
  let providers =
    _.map(providerList, (item) => {
      const centralStoreQty = getCentralStoreAvailableQty(item?.categorys, selectedEquipment);
      const centralStoreOption = {
        text: `${item.wardName}${centralStoreQty !== null ? ` (Qty: ${centralStoreQty})` : ``} `,
        value: item.wardId,
        tagId: item.wardId,
        categorys: item.categorys,
        wardId: item.wardId,
        provideWardName: item.wardName,
        isFloor: true,
        group: item.wardName,
        availableQty: centralStoreQty ?? 0,
      };
      const satelliteStoreOptions = getSatelliteStoreOptions(item, selectedEquipment);
      return [centralStoreOption, ...satelliteStoreOptions];
    }).flat() ?? [];

  if (type === 'lend') {
    providers = _.filter(providers, (item) => item.wardId === currentWardId);
  }
  // 過濾當前 擁有所有已選category 的provider
  if (_.some(equipmentAssets, (item) => item.category)) {
    providers = _.filter(providers, (provider) =>
      equipmentAssets
        .filter((item) => item.category)
        .every((assetItem) => provider.categorys.some((providerItem) => assetItem.category === providerItem.id))
    );
  }

  return refactorProviderListByGroup(providers);
};

// 计算每个 provider group 下的 availableQty 总和
const getProvidersTotalQty = (providersOptions) => {
  return providersOptions.reduce((sum, option) => {
    if (!sum[option.group]) {
      sum[option.group] = 0;
    }
    sum[option.group] += option.availableQty;
    return sum;
  }, {});
};

// 如果某个 group 只有一个 item 且该 item 的 isFloor 为 true，则从该 item 中删除 group 属性。
const refactorProviderListByGroup = (providerList = []) => {
  if (!_.isArray(providerList)) return [];
  const groups = _.groupBy(providerList, 'group');

  return _.map(providerList, (item) => {
    const groupItems = groups[item.group] || [];
    if (groupItems.length === 1 && groupItems[0].isFloor === true) {
      // 根据条件删除对象的 group 属性
      // eslint-disable-next-line no-unused-vars
      const { group, ...rest } = item;
      return { ...rest };
    }
    return item;
  });
};

// for Lend / Borrow Loan Request Popup Provider options - render group
const renderProviderGroup = (params, equipmentAssets, flag, providersOptions) => {
  const providersTotalQty = getProvidersTotalQty(providersOptions);
  return (
    <li key={`provider-group-header ${params.key}`}>
      {params.group && (
        <Box
          style={{
            padding: 8,
          }}
        >
          <Text fontWeight="600" color="#3e3e3e" fontSize="1rem">
            {`${params?.group} ${equipmentAssets?.[flag]?.category && `(Total: ${providersTotalQty[params.group]})`}`}
          </Text>
        </Box>
      )}
      <ul style={{ paddingLeft: 0 }}>{params.children}</ul>
    </li>
  );
};

// for handle Receipt Confirmation in Lend / Borrow Edit Loan Request
// add Receipt Confirmation
const handleMultiReceiptEnter = async (receipt, setMultiReceiptValue) => {
  if (!receipt?.value) return;
  const newVal = receipt?.value?.includes('H') ? receipt?.value?.slice(1) : receipt?.value;
  let nameFromApi = '';
  try {
    const { result } = await equipmentAPI.getStaffName({
      empId: newVal,
    });
    nameFromApi = result[0] ?? '';
  } catch (error) {
    console.log(`get staff name error: `, error);
  }
  const newReceipt = { ...receipt, value: nameFromApi || newVal };
  setMultiReceiptValue((preMultiReciept) => [...preMultiReciept, newReceipt]);
};

// delete Receipt Confirmation
const handleDeleteReceipt = (id, setMultiReceiptValue) => {
  setMultiReceiptValue((preMultiReciept) => _.filter(preMultiReciept, (item) => item.id !== id));
};

// 转换 Receipt Confirmation 的结构
const getMutiReceiptVal = (mutiReceiptVal, type) => {
  if (type === 'fromApi') {
    const receiptToArr =
      typeof mutiReceiptVal === 'string' && mutiReceiptVal?.length > 0 ? mutiReceiptVal?.split('||') : [];

    return _.map(receiptToArr, (item) => ({
      value: item,
      id: getUUID(),
    }));
  } else {
    const receiptValArr = _.map(mutiReceiptVal, (item) => item?.value);
    return receiptValArr.join('||');
  }
};

export {
  getNewRequestProviderOptions,
  getCentralStoreAvailableQty,
  handleMultiReceiptEnter,
  handleDeleteReceipt,
  getMutiReceiptVal,
  renderProviderGroup,
};
